import { Button as AntButton } from 'antd';
import { colors } from 'design';
import { forwardRef } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { createUseStyles } from 'react-jss';

import type { UiBaseProps } from './types';

/**
 * The button primitive.
 *
 * Replaces `GGButton`, whose variants were expressed by overloading antd's
 * `color` prop with values antd has never heard of (`color="discrete"`), and
 * which then had to fight its own theme with `!important` to make them stick.
 * Variants are now a closed set with names that say what the button is *for*.
 *
 * Buttons are flat: the old `0px 5px rgba(100,100,100,0.2)` hard offset shadow
 * is gone, and separation comes from fill contrast instead.
 *
 * ### Two structural constraints, both learned the hard way
 *
 * **It forwards its ref, and it renders exactly one element.** antd's overlay
 * components — `Dropdown`, `Popover`, `Tooltip`, `Popconfirm` — clone their
 * trigger child to attach a ref and an `onClick`. A component that swallows the
 * ref, or that wraps its button in another element, silently breaks every
 * overlay it triggers: the menu simply never opens.
 *
 * **The accent variants are styled with a class, not a nested theme.** antd's
 * `color` prop only accepts its own preset names, so mint and coral cannot be
 * passed directly, and the obvious fix is to wrap the button in a scoped
 * `ConfigProvider`. That wrapper is exactly the extra element the paragraph
 * above forbids — a cloned `ConfigProvider` receives the ref and `onClick` as
 * props and drops them on the floor. So those two variants use react-jss
 * classes instead, which is safe here: antd v6 emits its rules behind
 * `:where(...)` (zero specificity) and react-jss injects after antd, so a plain
 * class wins without an `!important`.
 */
export type ButtonVariant = 'primary' | 'accent' | 'highlight' | 'secondary' | 'ghost' | 'link' | 'danger';

export type ButtonProps = Omit<UiBaseProps, 'children'> & {
  children?: ReactNode;
  /** Defaults to `secondary` — the quiet fill, which is the common case. */
  variant?: ButtonVariant;
  size?: 'sm' | 'md' | 'lg';
  /** Fills the width of its container. */
  block?: boolean;
  loading?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  iconPosition?: 'start' | 'end';
  /** Marks a toggle button as chosen — draws an accent ring around it. */
  selected?: boolean;
  shape?: 'default' | 'circle' | 'round';
  htmlType?: 'button' | 'submit' | 'reset';
  href?: string;
  target?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  'aria-label'?: string;
  /**
   * Escape hatch for callers that set the attribute directly. Prefer `testId`.
   *
   * It has to be named explicitly rather than left to the props spread: the
   * spread happens before the attributes below, so an unnamed `data-testid`
   * would be silently overwritten by `data-testid={testId}` with `undefined` —
   * which is exactly how three test ids disappeared when `GGButton`, which
   * spread every prop it was given, was replaced by this.
   */
  'data-testid'?: string;
};

const SIZES = { sm: 'small', md: 'middle', lg: 'large' } as const;

/** How each variant maps onto antd's own `type` / `danger` props. */
const ANT_TYPES: Record<ButtonVariant, { type: 'primary' | 'default' | 'text' | 'link'; danger?: boolean }> = {
  primary: { type: 'primary' },
  // The accent variants ride on `default` and take their fill from a class, so
  // they do not inherit antd's primary colour underneath.
  accent: { type: 'default' },
  highlight: { type: 'default' },
  secondary: { type: 'default' },
  ghost: { type: 'text' },
  link: { type: 'link' },
  danger: { type: 'primary', danger: true },
};

const useStyles = createUseStyles({
  /** Mint. Label is deep teal, not white — white on mint is 1.8:1. */
  accent: {
    backgroundColor: colors.brand.accent,
    color: colors.brand.onAccent,
    borderColor: 'transparent',
    '&:hover:not(:disabled)': {
      backgroundColor: `${colors.brand.accentHover} !important`,
      color: `${colors.brand.onAccent} !important`,
    },
    '&:active:not(:disabled)': {
      backgroundColor: `${colors.brand.accentHover} !important`,
    },
  },
  /** Coral. Same story: white on coral is 2.3:1, so the label is deep teal. */
  highlight: {
    backgroundColor: colors.brand.highlight,
    color: colors.brand.onHighlight,
    borderColor: 'transparent',
    '&:hover:not(:disabled)': {
      backgroundColor: `${colors.brand.highlightHover} !important`,
      color: `${colors.brand.onHighlight} !important`,
    },
    '&:active:not(:disabled)': {
      backgroundColor: `${colors.brand.highlightHover} !important`,
    },
  },
});

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    block,
    loading,
    disabled,
    icon,
    iconPosition,
    selected,
    shape = 'default',
    htmlType = 'button',
    href,
    target,
    onClick,
    testId,
    className,
    style,
    children,
    'aria-label': ariaLabel,
    'data-testid': dataTestId,
    ...rest
  },
  ref,
) {
  const styles = useStyles();
  const { type, danger } = ANT_TYPES[variant];

  // The `!important`s in the hover rules above are the one place they are
  // warranted: antd's own `:hover` rule and ours have equal specificity, and
  // which wins would otherwise depend on injection order at runtime.
  const variantClass = variant === 'accent' ? styles.accent : variant === 'highlight' ? styles.highlight : undefined;

  // Inline rather than a class: this has to beat antd's own runtime-injected
  // rule, and an inline style does so without a guess about stylesheet
  // ordering. Safe here because a selection ring has no hover state.
  //
  // No `borderRadius` override: a toggle button must keep the same shape
  // selected or not — this used to force `radius.md`, which is exactly the
  // "selected buttons are pointy, unselected ones are pills" bug several
  // pages had to fight with a scoped `!important` override of their own.
  const selectedStyle: CSSProperties = selected ? { boxShadow: `inset 0 0 0 2px ${colors.border.accent}` } : {};

  return (
    <AntButton
      {...rest}
      ref={ref}
      type={type}
      danger={danger}
      size={SIZES[size]}
      block={block}
      loading={loading}
      disabled={disabled}
      icon={icon}
      iconPosition={iconPosition}
      shape={shape}
      htmlType={htmlType}
      href={href}
      target={target}
      onClick={onClick}
      className={[variantClass, className].filter(Boolean).join(' ') || undefined}
      style={{ ...selectedStyle, ...style }}
      aria-label={ariaLabel}
      aria-pressed={selected}
      /*
       * `aria-pressed` is the correct thing for assistive technology, but the
       * Playwright suite has ~75 assertions against `data-selected`, which
       * GGButton used to emit. Keeping it costs one attribute and means the
       * toggle state stays queryable without depending on ARIA semantics.
       * Only emitted when the button is actually a toggle.
       */
      data-selected={selected === undefined ? undefined : selected}
      data-testid={testId ?? dataTestId}
    >
      {children}
    </AntButton>
  );
});

export default Button;
